import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { OtpApi } from "@src/api/otp";

type Channel = "whatsapp" | "email";
type Step = "input" | "code";

interface Props {
  onSuccess: (token: string, roles: string[]) => void;
}

function decodeJwtPayload(token: string): Record<string, any> {
  try {
    const part = token.split(".")[1];
    const padded = part + "==".slice(0, (4 - (part.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

function extractRoles(payload: Record<string, any>): string[] {
  const roles =
    payload["cognito:groups"] ||
    payload["groups"] ||
    payload["roles"] ||
    payload["role"] ||
    [];
  return Array.isArray(roles) ? roles : [roles].filter(Boolean);
}

export const LoginPage: React.FC<Props> = ({ onSuccess }) => {
  const [tab, setTab] = useState<Channel>("whatsapp");
  const [step, setStep] = useState<Step>("input");
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [verifyError, setVerifyError] = useState("");

  const handleTabChange = (_: any, v: Channel) => {
    setTab(v);
    setStep("input");
    setIdentifier("");
    setCode("");
    setRequestError("");
    setVerifyError("");
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequesting(true);
    setRequestError("");
    try {
      if (tab === "whatsapp") {
        await OtpApi.requestWhatsapp(identifier);
      } else {
        await OtpApi.requestEmail(identifier);
      }
      setStep("code");
    } catch {
      setRequestError("Не удалось отправить код. Проверьте данные.");
    } finally {
      setRequesting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setVerifyError("");
    try {
      const res =
        tab === "whatsapp"
          ? await OtpApi.verifyWhatsapp(identifier, code)
          : await OtpApi.verifyEmail(identifier, code);
      const token = res.data.token;
      const payload = decodeJwtPayload(token);
      const roles: string[] = extractRoles(payload);
      console.debug("[login] JWT payload keys:", Object.keys(payload), "roles:", roles);
      onSuccess(token, roles);
    } catch {
      setVerifyError("Неверный код. Попробуйте ещё раз.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="#1a1a2e"
    >
      <Box
        bgcolor="white"
        borderRadius={2}
        p={4}
        width={400}
        boxShadow={4}
      >
        <Typography
          textAlign="center"
          color="#39d29f"
          fontWeight={900}
          fontSize={48}
          lineHeight={1}
        >
          Kwaaka
        </Typography>
        <Typography
          textAlign="center"
          color="text.secondary"
          mb={3}
          fontSize={16}
        >
          Управлять заказами просто!
        </Typography>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab label="WhatsApp" value="whatsapp" />
          <Tab label="Email" value="email" />
        </Tabs>

        {step === "input" ? (
          <Box component="form" onSubmit={handleRequestOtp}>
            {tab === "whatsapp" ? (
              <TextField
                fullWidth
                label="Номер телефона"
                placeholder="77001234567"
                type="tel"
                inputMode="numeric"
                value={identifier}
                onChange={(e) =>
                  setIdentifier(e.target.value.replace(/\D/g, "").slice(0, 11))
                }
                required
                error={identifier.length > 0 && identifier.length < 11}
                helperText={
                  identifier.length > 0 && identifier.length < 11
                    ? "Введите 11 цифр"
                    : undefined
                }
              />
            ) : (
              <TextField
                fullWidth
                label="Email"
                placeholder="you@example.com"
                type="email"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            )}
            {requestError && (
              <Typography color="error" fontSize={13} mt={1}>
                {requestError}
              </Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{ mt: 2 }}
              disabled={
                requesting ||
                (tab === "whatsapp" && identifier.length !== 11) ||
                (tab === "email" && !identifier.includes("@"))
              }
            >
              {requesting ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              {tab === "whatsapp"
                ? "Получить код в WhatsApp"
                : "Получить код на Email"}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleVerifyOtp}>
            <Typography color="text.secondary" textAlign="center" mb={2} fontSize={14}>
              Код отправлен{" "}
              {tab === "whatsapp" ? `в WhatsApp на ${identifier}` : `на ${identifier}`}
            </Typography>
            <TextField
              fullWidth
              label="Код подтверждения"
              placeholder="123456"
              type="tel"
              inputMode="numeric"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              required
              inputProps={{ maxLength: 6 }}
            />
            {verifyError && (
              <Typography color="error" fontSize={13} mt={1}>
                {verifyError}
              </Typography>
            )}
            <Box display="flex" gap={1} mt={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep("input");
                  setCode("");
                  setVerifyError("");
                }}
                disabled={verifying}
                sx={{ flex: 1 }}
              >
                {tab === "whatsapp" ? "Изменить номер" : "Изменить email"}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={code.length < 6 || verifying}
                sx={{ flex: 1 }}
              >
                {verifying ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : null}
                Войти
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
